import type { GroupedProperty } from "../services/emailService";

const TRAILING_SQFT_PATTERN = /\s*\(\s*\d[\d,]*\s*SF\s*\)\s*$/i;
// Matches a bed/bath prefix followed by a hyphen and a short alphanumeric model code.
// Examples kept: "A1 Deluxe" (no match), "B1" (no match), "A2J" (no match).
// Examples cleaned: "1 Bed/1 Bath-A2J" -> "A2J", "2 Bed/2 Bath - B3" -> "B3".
const BED_BATH_MODEL_PATTERN =
  /^\s*\d+\s*bed\s*\/\s*\d+\s*bath\s*[-\u2013\u2014]\s*([A-Z][A-Z0-9]{0,5})\s*$/i;

export const buildUnitEditKey = (propertyId: string, unitId: string): string =>
  `${propertyId}::${unitId}`;

export const sanitizeFloorPlanModelName = (modelName?: string): string => {
  const raw = (modelName || "").trim();
  const withoutSqft = raw.replace(TRAILING_SQFT_PATTERN, "").trim();
  const bedBathMatch = withoutSqft.match(BED_BATH_MODEL_PATTERN);
  if (bedBathMatch) return bedBathMatch[1].toUpperCase();
  return withoutSqft;
};

const updateUnitInGroupedProperties = (
  properties: GroupedProperty[],
  propertyId: string,
  unitId: string,
  updater: (unit: GroupedProperty["units"][number]) => GroupedProperty["units"][number]
): GroupedProperty[] =>
  properties.map((property) =>
    property.property_id !== propertyId
      ? property
      : {
          ...property,
          units: property.units.map((unit) =>
            unit.id === unitId ? updater(unit) : unit
          ),
        }
  );

export const updateFloorPlanModelName = (
  properties: GroupedProperty[],
  propertyId: string,
  unitId: string,
  modelName: string
): GroupedProperty[] =>
  updateUnitInGroupedProperties(properties, propertyId, unitId, (unit) => ({
    ...unit,
    model_name: modelName,
  }));

export const sanitizeFloorPlanModelNameForUnit = (
  properties: GroupedProperty[],
  propertyId: string,
  unitId: string
): GroupedProperty[] =>
  updateUnitInGroupedProperties(properties, propertyId, unitId, (unit) => ({
    ...unit,
    model_name: sanitizeFloorPlanModelName(unit.model_name),
  }));

export const setFloorPlanSqftVisible = (
  properties: GroupedProperty[],
  propertyId: string,
  unitId: string,
  visible: boolean
): GroupedProperty[] =>
  updateUnitInGroupedProperties(properties, propertyId, unitId, (unit) => ({
    ...unit,
    hide_square_feet_in_email: !visible,
  }));

export const isFloorPlanSqftVisible = (
  squareFeet?: number,
  hideSquareFeetInEmail?: boolean
): boolean => !!squareFeet && !hideSquareFeetInEmail;

